import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import 'rxjs/add/operator/map';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { AppUtils } from '@app/shared/util/app.utils';
import { Tool } from '@app/tool/model/tool.model';
import { ToolSet } from '@app/tool/model/toolSet.model';
import { Batch } from '@models/batch.model';
import { Slot } from '@models/slot.model';
import { LiftToolFilters } from '../models/lift-tool-filters.model';
import { from, of } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';

@Injectable()
export class BatchService {
    static readonly API_BATCHES_BASE_PATH = `${environment.baseURL}/api/batches`;

    constructor(private http: HttpClient) {
    }

    loadBatch(batchId: number, config: TableConfig) {
        const params = AppUtils.buildPageableParams(config);
        return this.http.get(`${BatchService.API_BATCHES_BASE_PATH}/${batchId}`, {params});
    }

    findSlotsWithMatchingTools(slots: Slot[], filters: LiftToolFilters) {
        if (!filters.toolUID && !filters.toolName && !filters.lineId) {
            return of(slots);
        }
        const config = new TableConfig();
        config.pager.pageSize = 1;
        config.upsertFilter('toolUid', filters.toolUID);
        config.upsertFilter('name', filters.toolName);
        config.upsertFilter('lineId', filters.lineId);
        const batches = slots.reduce((items, slot) => {
            slot.batches.forEach(batch => items.push({slotId: slot.id, batchId: batch.id}));
            return items;
        }, [] as {slotId: number, batchId: number}[]);
        return from(batches).pipe(
            mergeMap(item => this.loadBatch(item.batchId, config).pipe(
                map((result: any) => ({slotId: item.slotId, matches: result.tools && result.tools.total > 0}))
            ), 6),
            toArray(),
            map(results => slots.filter(slot => results.some(result => result.slotId === slot.id && result.matches)))
        );
    }

    createBatch(slotId: number, name: string, tools: any[], withStorage: boolean) {
        const toolIds = this.extractToolIds(tools);
        return this.http.post(`${BatchService.API_BATCHES_BASE_PATH}`, {slotId: slotId, toolIds: toolIds, name: name,
            withStorage: withStorage});
    }

    copyBatches(liftId: number, trayId: number, slotId: number, batchIds: number[]) {
        return this.http.post(`${BatchService.API_BATCHES_BASE_PATH}/copy`, {
            liftId: liftId, trayId: trayId, slotId: slotId, batchIds: batchIds
        });
    }

    duplicateBatch(slotId: number, id: number) {
        return this.http.post(`${BatchService.API_BATCHES_BASE_PATH}/${id}/duplicate`, slotId);
    }

    addToolsToBatch(batchId: number, tools: any[], withStorage: boolean) {
        const toolIds = this.extractToolIds(tools);
        return this.http.patch(`${BatchService.API_BATCHES_BASE_PATH}/${batchId}`, {toolIds: toolIds, operation: 'add',
            withStorage: withStorage});
    }

    deleteBatch(batchId: number) {
        return this.http.delete(`${BatchService.API_BATCHES_BASE_PATH}/${batchId}`);
    }

    removeToolsFromBatch(batchId: number, tools: any[]) {
        const toolIds = this.extractToolIds(tools);
        return this.http.patch(`${BatchService.API_BATCHES_BASE_PATH}/${batchId}`, {toolIds: toolIds, operation: 'remove'});
    }

    updateCartBatchTools(batchId: number, tools: any[], cart: number) {
        const toolIds = this.extractToolIds(tools);
        return this.http.patch(`${BatchService.API_BATCHES_BASE_PATH}/${batchId}/tools`, {toolIds: toolIds, cart: cart});
    }

    updateBatch(batch: Batch) {
        return this.http.patch(`${BatchService.API_BATCHES_BASE_PATH}/${batch.id}`, {name: batch.name});
    }

    private extractToolIds(tools: any[]) {
        const toolIds = [];
        tools.forEach((tool: Tool | ToolSet) => {
            if (tool instanceof Tool) {
                toolIds.push(tool.id);
            } else if (tool instanceof ToolSet) {
                tool.tools.map(x => x.id).forEach(toolId => {
                    toolIds.push(toolId);
                });
            }
        });
        return toolIds;
    }
}
